// Parse / format / humanize a constrained subset of RFC-5545 RRULE strings.
//
// Supported shapes match what the smart-todo LLM prompt emits:
//   FREQ=DAILY[;INTERVAL=N]
//   FREQ=WEEKLY[;INTERVAL=N][;BYDAY=DD,...]
//   FREQ=MONTHLY[;INTERVAL=N]
//   FREQ=MONTHLY;BYMONTHDAY=N                (N ∈ 1..31, -1)
//   FREQ=MONTHLY;BYDAY=NDD                   (N ∈ 1..5, -1, -2; DD ∈ MO..SU)
//   FREQ=YEARLY[;INTERVAL=N]
//
// Every shape above may also carry UNTIL=<UTC date-time>|<date> or COUNT=N.
// Anything else (BYMONTH, multi-BYDAY in MONTHLY, etc.) parses to
// { kind: 'custom', raw } so the UI can render read-only "Custom" without
// destroying the server-side value.

export type Weekday = 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU';

export const WEEKDAYS: Weekday[] = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

export type MonthlyMode =
  | { kind: 'byMonthDay'; day: number } // 1..31 or -1 (last)
  | { kind: 'byWeekday'; ordinal: number; day: Weekday }; // ordinal ∈ 1..5, -1, -2

export type EndCondition = {
  until?: Date | null;
  count?: number | null;
};

export type RecurrenceState =
  | { kind: 'none' }
  | ({ kind: 'daily'; interval: number } & EndCondition)
  | ({ kind: 'weekly'; interval: number; byDay: Weekday[] } & EndCondition)
  | ({ kind: 'monthly'; interval: number; mode: MonthlyMode | null } & EndCondition)
  | ({ kind: 'yearly'; interval: number } & EndCondition)
  | { kind: 'custom'; raw: string };

const WEEKDAY_SET = new Set<Weekday>(WEEKDAYS);

function parseParts(rule: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of rule.split(';')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    const k = part.slice(0, eq).trim().toUpperCase();
    const v = part.slice(eq + 1).trim();
    if (k) out[k] = v;
  }
  return out;
}

function parseInterval(s: string | undefined): number | null {
  if (s === undefined) return 1;
  const n = Number(s);
  if (!Number.isInteger(n) || n < 1) return null;
  return n;
}

function parseByDayList(s: string): Weekday[] | null {
  const out: Weekday[] = [];
  for (const tok of s.split(',')) {
    const t = tok.trim().toUpperCase();
    if (!WEEKDAY_SET.has(t as Weekday)) return null;
    out.push(t as Weekday);
  }
  return out;
}

// Parse a single BYDAY token like "1MO", "-1FR", "2TU", "MO".
function parseByDayOrdinal(tok: string): { ordinal: number | null; day: Weekday } | null {
  const t = tok.trim().toUpperCase();
  const m = /^(-?\d+)?([A-Z]{2})$/.exec(t);
  if (!m) return null;
  const day = m[2] as Weekday;
  if (!WEEKDAY_SET.has(day)) return null;
  const ordinal = m[1] === undefined ? null : Number(m[1]);
  if (ordinal !== null && !Number.isInteger(ordinal)) return null;
  return { ordinal, day };
}

// Parse RFC-5545 UNTIL: "YYYYMMDD" (date) or "YYYYMMDDTHHMMSSZ" (UTC
// date-time). Floating local "YYYYMMDDTHHMMSS" tolerated as UTC.
// Returns null on malformed input — caller treats as custom.
function parseUntil(raw: string): Date | null {
  const s = raw.trim().toUpperCase();
  const m = /^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})(Z)?)?$/.exec(s);
  if (!m) return null;
  const yyyy = Number(m[1]);
  const mm = Number(m[2]);
  const dd = Number(m[3]);
  const hh = Number(m[4] ?? '23');
  const mi = Number(m[5] ?? '59');
  const ss = Number(m[6] ?? '59');
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  const d = new Date(Date.UTC(yyyy, mm - 1, dd, hh, mi, ss));
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function parseCount(raw: string): number | null {
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1) return null;
  return n;
}

// Two-digit zero-pad.
function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function formatUntil(d: Date): string {
  const y = d.getUTCFullYear();
  const m = pad2(d.getUTCMonth() + 1);
  const day = pad2(d.getUTCDate());
  const hh = pad2(d.getUTCHours());
  const mi = pad2(d.getUTCMinutes());
  const ss = pad2(d.getUTCSeconds());
  return `${y}${m}${day}T${hh}${mi}${ss}Z`;
}

// Append UNTIL or COUNT (but not both) to the parts list, in canonical
// order. RFC-5545 forbids combining them; if a state somehow has both,
// UNTIL wins.
function appendEnd(parts: string[], state: EndCondition): void {
  if (state.until) {
    parts.push(`UNTIL=${formatUntil(state.until)}`);
  } else if (state.count !== undefined && state.count !== null) {
    parts.push(`COUNT=${state.count}`);
  }
}

export function parseRRule(input: string | null | undefined): RecurrenceState {
  const raw = (input || '').trim();
  if (!raw) return { kind: 'none' };

  // Tolerate "RRULE:FREQ=..." prefix (not emitted by our LLM, but possible via imports).
  const body = raw.startsWith('RRULE:') ? raw.slice(6) : raw;
  const parts = parseParts(body);
  const freq = (parts.FREQ || '').toUpperCase();

  if (!freq) return { kind: 'custom', raw };

  const interval = parseInterval(parts.INTERVAL);
  if (interval === null) return { kind: 'custom', raw };

  // Reject any field we don't know how to round-trip.
  const known = new Set(['FREQ', 'INTERVAL', 'BYDAY', 'BYMONTHDAY', 'UNTIL', 'COUNT']);
  for (const k of Object.keys(parts)) {
    if (!known.has(k)) return { kind: 'custom', raw };
  }

  // UNTIL and COUNT are mutually exclusive per RFC-5545. If both appear,
  // the rule is malformed — fall to custom.
  if (parts.UNTIL !== undefined && parts.COUNT !== undefined) {
    return { kind: 'custom', raw };
  }
  const end: EndCondition = {};
  if (parts.UNTIL !== undefined) {
    const u = parseUntil(parts.UNTIL);
    if (!u) return { kind: 'custom', raw };
    end.until = u;
  }
  if (parts.COUNT !== undefined) {
    const c = parseCount(parts.COUNT);
    if (c === null) return { kind: 'custom', raw };
    end.count = c;
  }

  switch (freq) {
    case 'DAILY': {
      if (parts.BYDAY !== undefined || parts.BYMONTHDAY !== undefined) {
        return { kind: 'custom', raw };
      }
      return { kind: 'daily', interval, ...end };
    }
    case 'WEEKLY': {
      if (parts.BYMONTHDAY !== undefined) return { kind: 'custom', raw };
      let byDay: Weekday[] = [];
      if (parts.BYDAY !== undefined) {
        const parsed = parseByDayList(parts.BYDAY);
        if (!parsed) return { kind: 'custom', raw };
        byDay = parsed;
      }
      return { kind: 'weekly', interval, byDay, ...end };
    }
    case 'MONTHLY': {
      const hasByMonthDay = parts.BYMONTHDAY !== undefined;
      const hasByDay = parts.BYDAY !== undefined;
      if (hasByMonthDay && hasByDay) return { kind: 'custom', raw };
      if (!hasByMonthDay && !hasByDay) {
        return { kind: 'monthly', interval, mode: null, ...end };
      }
      if (hasByMonthDay) {
        const n = Number(parts.BYMONTHDAY);
        if (!Number.isInteger(n)) return { kind: 'custom', raw };
        if (n === 0 || n < -1 || n > 31) return { kind: 'custom', raw };
        return {
          kind: 'monthly',
          interval,
          mode: { kind: 'byMonthDay', day: n },
          ...end,
        };
      }
      // hasByDay — must be a single ordinal+weekday token.
      if (parts.BYDAY.includes(',')) return { kind: 'custom', raw };
      const od = parseByDayOrdinal(parts.BYDAY);
      if (!od || od.ordinal === null) return { kind: 'custom', raw };
      const okOrdinal =
        od.ordinal === 1 ||
        od.ordinal === 2 ||
        od.ordinal === 3 ||
        od.ordinal === 4 ||
        od.ordinal === 5 ||
        od.ordinal === -1 ||
        od.ordinal === -2;
      if (!okOrdinal) return { kind: 'custom', raw };
      return {
        kind: 'monthly',
        interval,
        mode: { kind: 'byWeekday', ordinal: od.ordinal, day: od.day },
        ...end,
      };
    }
    case 'YEARLY': {
      if (parts.BYDAY !== undefined || parts.BYMONTHDAY !== undefined) {
        return { kind: 'custom', raw };
      }
      return { kind: 'yearly', interval, ...end };
    }
    default:
      return { kind: 'custom', raw };
  }
}

export function formatRRule(state: RecurrenceState): string {
  switch (state.kind) {
    case 'none':
      return '';
    case 'custom':
      return state.raw;
    case 'daily': {
      const parts = ['FREQ=DAILY'];
      if (state.interval > 1) parts.push(`INTERVAL=${state.interval}`);
      appendEnd(parts, state);
      return parts.join(';');
    }
    case 'weekly': {
      const parts = ['FREQ=WEEKLY'];
      if (state.interval > 1) parts.push(`INTERVAL=${state.interval}`);
      if (state.byDay.length > 0) {
        // Canonical weekday order (MO..SU) makes round-trip stable.
        const sorted = [...state.byDay].sort(
          (a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b),
        );
        parts.push(`BYDAY=${sorted.join(',')}`);
      }
      appendEnd(parts, state);
      return parts.join(';');
    }
    case 'monthly': {
      const parts = ['FREQ=MONTHLY'];
      if (state.interval > 1) parts.push(`INTERVAL=${state.interval}`);
      if (state.mode) {
        if (state.mode.kind === 'byMonthDay') {
          parts.push(`BYMONTHDAY=${state.mode.day}`);
        } else {
          parts.push(`BYDAY=${state.mode.ordinal}${state.mode.day}`);
        }
      }
      appendEnd(parts, state);
      return parts.join(';');
    }
    case 'yearly': {
      const parts = ['FREQ=YEARLY'];
      if (state.interval > 1) parts.push(`INTERVAL=${state.interval}`);
      appendEnd(parts, state);
      return parts.join(';');
    }
  }
}

// Map Weekday → long/short human label.
const WEEKDAY_LONG: Record<Weekday, string> = {
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
  SU: 'Sunday',
};
const WEEKDAY_SHORT: Record<Weekday, string> = {
  MO: 'Mon',
  TU: 'Tue',
  WE: 'Wed',
  TH: 'Thu',
  FR: 'Fri',
  SA: 'Sat',
  SU: 'Sun',
};

const ORDINAL_LABEL: Record<number, string> = {
  [-2]: 'second-to-last',
  [-1]: 'last',
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
  5: 'fifth',
};

function ordinalDay(n: number): string {
  if (n === -1) return 'last day';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function humanDate(d: Date): string {
  return `${MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

function humanEnd(state: EndCondition): string {
  if (state.until) return ` until ${humanDate(state.until)}`;
  if (state.count !== undefined && state.count !== null) {
    return state.count === 1 ? ` for 1 occurrence` : ` for ${state.count} occurrences`;
  }
  return '';
}

export function formatHuman(state: RecurrenceState): string {
  switch (state.kind) {
    case 'none':
      return 'None';
    case 'custom':
      return 'Custom';
    case 'daily': {
      const base =
        state.interval === 1 ? 'Daily' : `Every ${state.interval} days`;
      return base + humanEnd(state);
    }
    case 'weekly': {
      const every =
        state.interval === 1
          ? 'Weekly'
          : state.interval === 2
            ? 'Every other week'
            : `Every ${state.interval} weeks`;
      let base: string;
      if (state.byDay.length === 0) {
        base = every;
      } else if (
        state.byDay.length === 5 &&
        ['MO', 'TU', 'WE', 'TH', 'FR'].every((d) => state.byDay.includes(d as Weekday))
      ) {
        base = state.interval === 1 ? 'Weekdays' : `${every} on weekdays`;
      } else if (
        state.byDay.length === 2 &&
        state.byDay.includes('SA') &&
        state.byDay.includes('SU')
      ) {
        base = state.interval === 1 ? 'Weekends' : `${every} on weekends`;
      } else {
        const sorted = [...state.byDay].sort(
          (a, b) => WEEKDAYS.indexOf(a) - WEEKDAYS.indexOf(b),
        );
        const names = sorted.map((d) => WEEKDAY_SHORT[d]).join(', ');
        base = `${every} on ${names}`;
      }
      return base + humanEnd(state);
    }
    case 'monthly': {
      const every =
        state.interval === 1
          ? 'Monthly'
          : state.interval === 2
            ? 'Every other month'
            : `Every ${state.interval} months`;
      let base: string;
      if (!state.mode) {
        base = every;
      } else if (state.mode.kind === 'byMonthDay') {
        base = `${every} on the ${ordinalDay(state.mode.day)}`;
      } else {
        const ord = ORDINAL_LABEL[state.mode.ordinal] || `${state.mode.ordinal}th`;
        base = `${every} on the ${ord} ${WEEKDAY_LONG[state.mode.day]}`;
      }
      return base + humanEnd(state);
    }
    case 'yearly': {
      const base =
        state.interval === 1 ? 'Yearly' : `Every ${state.interval} years`;
      return base + humanEnd(state);
    }
  }
}
